import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import styles from "./css/BehaviorAnalysis.module.css";
import { AllCakes } from "../components/AllCakes";
import { AllCakesDemo } from '../components/AllCakesDemoLogic';
// import { UserHeader } from "../components/UserHeader";
import { Footer } from "../components/footer";
import { safeLogger } from "../utils/logger";
import { showConfirm } from "../utils/notifications";
import { baseURL } from "../services/axios";
import { useSelector } from "react-redux";


const initialSituation = { situation: "", result: "", analyzed: false, loading: false, behavior: "", path: "" };

// פונקציה לפירוק התשובה של GPT לשלושה חלקים
const parseGptResponse = (response: string) => {
  // נסה לפרק לפי מבנה מובנה
  const lines = response.split('\n').filter(line => line.trim());
  
  let behavior = "";
  let path = "";
  let result = "";
  
  // חיפוש לפי מילות מפתח
  for (const line of lines) {
    const cleanLine = line.trim();
    if (cleanLine.includes('התנהגות') || cleanLine.includes('behavior')) {
      behavior = cleanLine.replace(/^[^:]*:/, '').trim();
    } else if (cleanLine.includes('נסיבות') || cleanLine.includes('סיבה') || cleanLine.includes('path')) {
      path = cleanLine.replace(/^[^:]*:/, '').trim();
    } else if (cleanLine.includes('תוצאה') || cleanLine.includes('result')) {
      result = cleanLine.replace(/^[^:]*:/, '').trim();
    }
  }
  
  // אם לא נמצא מבנה מובנה, חלק לשלושה חלקים שווים
  if (!behavior && !path && !result) {
    const parts = response.split('\n\n');
    if (parts.length >= 3) {
      behavior = parts[0].trim();
      path = parts[1].trim();
      result = parts[2].trim();
    } else {
      // אם אין חלוקה ברורה, שים הכל בתוצאה
      result = response;
    }
  }
  
  return { behavior, path, result };
};

export const BehaviorAnalysis = () => {
  const analystCode = useSelector((state: any) => state.auth.user?.code);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [childName, setChildName] = useState(location.state?.childName || "");
  const [boxes, setBoxes] = useState([{ ...initialSituation }]);
  const [sessionFinished, setSessionFinished] = useState(false);
  const [isFinishingSession, setIsFinishingSession] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // הוספת CSS לאנימציה
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    // אם אין שם מהניווט, נטען מהשרת
    if (!childName && id) {
      fetch(`${baseURL}/child/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
        .then(res => res.json())
        .then(data => setChildName(`${data.fname} ${data.lname}`))
        .catch(() => setChildName("שם הילד"));
    }
  }, [childName, id]);

  const handleSituationChange = (idx: number, value: string) => {
    const newBoxes = [...boxes];
    newBoxes[idx].situation = value;
    
    // אם השדה ריק, לא נאפס את התוצאות - מאפשר תיקון שגיאות בתוכן בלי לאבד תוצאות
    if (value.trim() === "") {
      newBoxes[idx].analyzed = false;
      newBoxes[idx].loading = false;
    } else if (newBoxes[idx].result && newBoxes[idx].result.includes("שגיאה")) {
      // אם היתה שגיאה קודמת, נאפס את המצב כדי לאפשר ניתוח מחדש
      newBoxes[idx].analyzed = false;
      newBoxes[idx].loading = false;
      newBoxes[idx].behavior = "";
      newBoxes[idx].path = "";
      newBoxes[idx].result = "";
    } else if (newBoxes[idx].analyzed) {
      // אם כבר יש תוצאות תקינות, נדרוש אישור לניתוח מחדש
      if (showConfirm("האם לאפס את תוצאות הניתוח הקודמות ולנתח מחדש?")) {
        newBoxes[idx].analyzed = false;
        newBoxes[idx].loading = false;
        newBoxes[idx].behavior = "";
        newBoxes[idx].path = "";
        newBoxes[idx].result = "";
      }
    }
    
    setBoxes(newBoxes);
  };

  // const handleAnalyze = async (idx: number) => {
  //   const newBoxes = [...boxes];
  //   newBoxes[idx].loading = true;
  //   setBoxes([...newBoxes]);
    // דימוי עיבוד בלבד
    // setTimeout(() => {
    //   newBoxes[idx] = {
    //     ...newBoxes[idx],
    //     behavior: 'דוגמה להתנהגות',
    //     path: 'דוגמה לנסיבות',
    //     result: 'דוגמה לתוצאה',
    //     analyzed: true,
    //     loading: false
    //   };
    //   setBoxes([...newBoxes]);
    // }, 1200);
    // קוד אמיתי לשליחה ל-GPT:
const handleAnalyze = async (idx: number) => {
  const newBoxes = [...boxes];
  newBoxes[idx].loading = true;
  setBoxes([...newBoxes]);

  try {
    console.log("▶️ Sending request to GPT server...");

    const response = await fetch(`${baseURL}/gpt/ask`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        Message: boxes[idx].situation,
       AnalystCode: analystCode,
      }),
    });

    console.log("⬅️ GPT Response Status:", response.status);

    // ---  טיפול בשגיאות מהשרת ---
    if (!response.ok) {
      let errorData = null;

      try {
        errorData = await response.json();
      } catch {}

      //  נגמרו הפרומפטים
      if (errorData?.error === "PROMPT_LIMIT_EXCEEDED") {
        alert("נגמרו לך הפרומפטים! לרכישת חבילה חדשה — גשי לעמוד החבילות.");
        newBoxes[idx].loading = false;
        setBoxes([...newBoxes]);
        return;
      }

      // שגיאה אחרת
      newBoxes[idx].result = "שגיאה בניתוח - נסי שוב.";
      newBoxes[idx].loading = false;
      setBoxes([...newBoxes]);
      return;
    }

    // --- ✔️ הצלחה: קבלת תשובה מהשרת ---
    const gptText = await response.text();
    console.log("GPT RAW RESPONSE:", gptText);

    const parsed = parseGptResponse(gptText);

    newBoxes[idx].behavior = parsed.behavior;
    newBoxes[idx].path = parsed.path;
    newBoxes[idx].result = parsed.result;

    const isValid =
      parsed.behavior &&
      parsed.path &&
      parsed.result &&
      !parsed.result.includes("שגיאה");

    // --- ✔️ שמירה ל־DB ---
    if (isValid) {
      await saveAnalysisResult(
        boxes[idx].situation,
        parsed.behavior,
        parsed.path,
        parsed.result
      );
    }

  } catch (err) {
    console.error("❌ Server connection error:", err);
    newBoxes[idx].result = "שגיאה בחיבור לשרת";
  }

  newBoxes[idx].analyzed = true;
  newBoxes[idx].loading = false;
  setBoxes([...newBoxes]);
};


  const saveAnalysisResult = async (situation: string, behavior: string, path: string, result: string) => {
    try {
      safeLogger.log('Starting database save process');
      
      // בדיקה אם כבר יש סשן פעיל לילד הזה
      let sessionId = localStorage.getItem(`sessionId_${id}`);
      
      safeLogger.log(sessionId ? 'Found existing session' : 'No existing session found');
      
      // אם אין סשן - צור סשן חדש
      if (!sessionId) {
        safeLogger.log('Creating new session for child');
        // יצירת סשן חדש
        const sessionResponse = await fetch(`${baseURL}/childsession`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            childCode: parseInt(id || '0'),
            date: new Date().toISOString(),
            responses: []
          }),
        });

        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          sessionId = sessionData.id.toString();
          localStorage.setItem(`sessionId_${id}`, sessionId as string);
          setCurrentSessionId(sessionId);
        } else {
          console.error('❌ שגיאה ביצירת סשן:', sessionResponse.status, sessionResponse.statusText);
        }
      } else {
        setCurrentSessionId(sessionId);
      }

      // שמירת התוצאה
      if (sessionId) {
        const modelResponse = await fetch(`${baseURL}/modelresponse`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            sentence: situation,
            path: path,
            behavior: behavior,
            result: result,
            childSessionId: parseInt(sessionId)
          }),
        });

        if (modelResponse.ok) {
          const savedResponse = await modelResponse.json();
        } else {
          console.error('❌ שגיאה בשמירת תוצאה:', modelResponse.status, modelResponse.statusText);
        }
      } else {
        console.error('❌ לא ניתן לשמור תוצאה - אין סשן זמין');
      }
    } catch (error) {
      console.error('❌ שגיאה כללית בשמירת תוצאה:', error);
    }
  };

  const handleFinishSession = () => {
    setIsFinishingSession(true);
    setTimeout(() => {
      // מחיקת הסשן הנוכחי מה-localStorage
      localStorage.removeItem(`sessionId_${id}`);
      // סימון שהסשן הסתיים (לא מאפסים את הקופסאות)
      setSessionFinished(true);
      setIsFinishingSession(false);
    }, 1500);
  };

  const handleAddBox = () => {
    setBoxes([...boxes, { ...initialSituation }]);
  };

  // בדיקה האם כל הריבועים נותחו ואין אף אחד במצב loading
  const allAnalyzed = boxes.every(box => box.analyzed);
  const anyLoading = boxes.some(box => box.loading);
  
  // בדיקה אם יש שגיאה באחד הריבועים
  const hasError = boxes.some(box => 
    box.analyzed && box.result && (box.result.includes("שגיאה") || !box.behavior)
  );
  
  // האם יש לפחות ריבוע אחד עם תוצאות תקינות
  const hasValidResults = boxes.some(box => 
    box.analyzed && box.behavior && box.path && box.result && 
    !box.result.includes("שגיאה")
  );

  return (
    <div className={styles.pageWrapper} style={{ 
      background: 'transparent',
      minHeight: '100vh'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* <UserHeader /> */}
      <div className={styles.contentWrapper}>
        <h2 style={{
          textAlign: 'center', 
          fontSize: '1.8rem', 
          marginBottom: '1.5rem',
          marginTop: '3rem',
          color: '#471f1f',
          fontWeight: 600,
          position: 'relative',
          paddingBottom: '12px'
        }}>
          ניתוח התנהגות - {childName}
          <span style={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80px',
            height: '3px',
            background: 'linear-gradient(to right, #a2b087, #ba9484)',
            borderRadius: '2px',
            display: 'block'
          }}></span>
        </h2>
                                      <div className={styles.betaBanner}>
<p>
  שימי לב: כלי ה-AI נמצא בשלבי פיתוח, ולכן ייתכנו תשובות שאינן מדויקות במקרים מסוימים.<br />
  אם נתקלת בתשובה שגויה – נשמח מאוד לשמוע.<br />
  מומלץ לשתף אותנו בסיטואציה, בתשובה שציפית לקבל ובתשובה שהתקבלה בפועל.<br />
  כך תוכלי לקחת חלק פעיל בשיפור ופיתוח המערכת✨.
</p>
 </div>

        <div style={{ maxWidth: '1400px', margin: '0 auto', direction: 'rtl' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: boxes.length === 1 ? '2fr 1fr' : 'repeat(2, 1fr)',
            gap: '30px',
            marginBottom: '30px'
          }}>
            {boxes.map((box, idx) => (
              <div key={idx} style={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                borderRadius: '15px',
                padding: '25px',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                width: '100%',
                height: '500px',
                minWidth: '500px'
              }}>
            <div style={{ marginBottom: '10px' }}>
              <label style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#471f1f',
                textAlign: 'right',
                display: 'block',
                marginBottom: '5px'
              }}>
                מקרה {idx + 1}:
              </label>
              <textarea
                style={{
                  height: '120px',
                  padding: '12px',
                  border: '2px solid #d2e0cc',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'inherit',
                  resize: 'none',
                  direction: 'rtl',
                  backgroundColor: '#fafafa',
                  width: '100%',
                  overflow: 'auto'
                }}
                value={box.situation}
                onChange={e => handleSituationChange(idx, e.target.value)}
                placeholder="תארי את המקרה ברצף :נסיבות → התנהגות → תוצאה
לדוגמה:הבזמן פעילות קבוצתית, הילד קם ממקומו והתחיל להסתובב בחדר, והצוות המשיך בפעילות כרגיל.
ככל שהתיאור יהיה רציף וברור יותר – הניתוח יהיה מדויק יותר.
"
                disabled={box.loading}
              />
            </div>
            
            <div style={{ marginBottom: '10px' }}>
              <button
                onClick={() => handleAnalyze(idx)}
                disabled={!box.situation.trim() || box.analyzed || box.loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: box.analyzed ? '#a2b087' : '#d2e0cc',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: (!box.analyzed && box.situation && !box.loading) ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  opacity: (!box.analyzed && box.situation && !box.loading) ? 1 : 0.6,
                  transition: 'all 0.3s ease',
                  width: '100%'
                }}
                title={box.analyzed ? "הניתוח כבר בוצע. ערוך את המקרה לניתוח חדש." : ""}
              >
                {box.loading ? (
                  <span>🔄 מעבד...</span>
                ) : box.analyzed ? "✓ נותח" : "נתח התנהגות"}
              </button>
            </div>
            
            <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
              <label style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#471f1f',
                textAlign: 'right',
                display: 'block',
                marginBottom: '5px'
              }}>
                תוצאות הניתוח:
              </label>
              <div style={{
                flex: '1',
                padding: '10px',
                backgroundColor: box.result && box.result.includes('שגיאה') ? '#fff5f5' : '#f8fffe',
                border: `2px solid ${box.result && box.result.includes('שגיאה') ? '#fca5a5' : '#d4ecee'}`,
                borderRadius: '8px',
                fontSize: '14px',
                direction: 'rtl',
                overflow: 'auto'
              }}>
                {box.loading ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    gap: '10px'
                  }}>
                    <div style={{
                      width: '30px',
                      height: '30px',
                      border: '3px solid #f3f3f3',
                      borderTop: '3px solid #a2b087',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <span style={{ color: '#666', fontSize: '12px' }}>מנתח את המקרה...</span>
                  </div>
                ) : (
                  <>
                    {box.result && box.result.includes('שגיאה') ? (
                      <div style={{ color: '#dc2626' }}>
                        <strong>שגיאה:</strong> {box.result}
                        <div style={{ marginTop: '8px', fontSize: '12px' }}>
                          יש לערוך את תוכן המקרה ולנסות שוב.
                        </div>
                      </div>
                    ) : (
                      <>
                        {box.behavior && (
                          <div style={{ marginBottom: '6px' }}>
                            <strong style={{ color: '#471f1f' }}>התנהגות:</strong> 
                            <div style={{ marginTop: '2px' }}>{box.behavior}</div>
                          </div>
                        )}
                        {box.path && (
                          <div style={{ marginBottom: '6px' }}>
                            <strong style={{ color: '#471f1f' }}>נסיבות:</strong> 
                            <div style={{ marginTop: '2px' }}>{box.path}</div>
                          </div>
                        )}
                        {box.result && (
                          <div style={{ marginBottom: '6px' }}>
                            <strong style={{ color: '#471f1f' }}>תוצאה:</strong> 
                            <div style={{ marginTop: '2px' }}>{box.result}</div>
                          </div>
                        )}
                        {!box.behavior && !box.path && !box.result && (
                          <div style={{ 
                            color: '#666', 
                            textAlign: 'center',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}>
                            הזן תוכן ולחץ על "נתח התנהגות"
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
            ))}
            
            {boxes.length === 1 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                borderRadius: '15px',
                padding: '20px',
                border: '2px dashed rgba(162, 176, 135, 0.4)'
              }}>
                <button
                  className={`${styles.mainButton} ${styles.buttonPeach}`}
                  onClick={handleAddBox}
                  disabled={!allAnalyzed || anyLoading || sessionFinished || hasError}
                  style={{ 
                    opacity: (allAnalyzed && !anyLoading && !sessionFinished && !hasError) ? 1 : 0.5, 
                    cursor: (allAnalyzed && !anyLoading && !sessionFinished && !hasError) ? "pointer" : "not-allowed",
                    width: '200px',
                    marginBottom: '10px'
                  }}
                  title={hasError ? "יש לתקן את השגיאות לפני הוספת מקרה חדש" : ""}
                >
                  + הוסף מקרה חדש
                </button>
                
                <button 
                  className={`${styles.mainButton} ${styles.buttonBrown}`}
                  onClick={handleFinishSession}
                  disabled={sessionFinished || !hasValidResults || anyLoading}
                  style={{ 
                    opacity: (!sessionFinished && hasValidResults && !anyLoading) ? 1 : 0.5,
                    cursor: (!sessionFinished && hasValidResults && !anyLoading) ? "pointer" : "not-allowed",
                    width: '200px'
                  }}
                  title={!hasValidResults ? "יש צורך בלפחות תוצאה אחת תקינה לפני סיום הסשן" : ""}
                >
                  {sessionFinished ? "הסשן הסתיים" : "סיים וצפה בתוצאות"}
                </button>
              </div>
            )}
          </div>
          
          {boxes.length > 1 && (
            <div className={styles.buttonContainer}>
              <button
                className={`${styles.mainButton} ${styles.buttonPeach}`}
                onClick={handleAddBox}
                disabled={!allAnalyzed || anyLoading || sessionFinished || hasError}
                style={{ 
                  opacity: (allAnalyzed && !anyLoading && !sessionFinished && !hasError) ? 1 : 0.5, 
                  cursor: (allAnalyzed && !anyLoading && !sessionFinished && !hasError) ? "pointer" : "not-allowed" 
                }}
                title={hasError ? "יש לתקן את השגיאות לפני הוספת מקרה חדש" : ""}
              >
                + הוסף מקרה חדש
              </button>
              
              <button 
                className={`${styles.mainButton} ${styles.buttonBrown}`}
                onClick={handleFinishSession}
                disabled={sessionFinished || !hasValidResults || anyLoading}
                style={{ 
                  opacity: (!sessionFinished && hasValidResults && !anyLoading) ? 1 : 0.5,
                  cursor: (!sessionFinished && hasValidResults && !anyLoading) ? "pointer" : "not-allowed"
                }}
                title={!hasValidResults ? "יש צורך בלפחות תוצאה אחת תקינה לפני סיום הסשן" : ""}
              >
                {isFinishingSession ? "מעבד נתונים..." : sessionFinished ? "הסשן הסתיים" : "סיים וצפה בתוצאות"}
              </button>
            </div>
          )}
        </div>
        {sessionFinished && (
          currentSessionId && currentSessionId !== 'FAKE_SESSION_ID' ? (
            <AllCakes childName={childName} sessionId={currentSessionId} />
          ) : (
            <AllCakesDemo childName={childName} />
          )
        )}
      </div>
    </div>
  );
};
